import convertRTEImages, { modifyNote } from 'utils/convertRTEImages'
import { uploadToCloudinary } from 'components/FileUploader/fileUploadUtils'
import { cloudinaryPublicId } from 'utils/cloudinaryPublicId'
import type { LibraryImage } from 'utils/libraryImages'

/** The Cloudinary response fields the note surfaces actually store. */
export interface UploadedNoteImage {
	public_id: string
	original_filename: string
	bytes: number
	secure_url: string
	format: string
	[key: string]: any
}

/**
 * Turns composer HTML into storable HTML.
 *
 * Images typed or dropped into the composer arrive as base64 embeds. They are
 * uploaded here and swapped for their Cloudinary URLs, and the uploads are
 * handed back so the caller can mirror them into its own gallery collection.
 *
 * The one piece of the note-create path that every notes surface shares. Each
 * surface differs only in which collection it writes the resulting rows to, so
 * that half stays in the surface and this half stays here.
 *
 * `modifyNote` swaps the nth `data:image` for `uploaded[n]` positionally, so the
 * upload loop must complete before it runs. A failed upload therefore rejects
 * the whole submit rather than writing a note with a half-swapped body.
 */
export const prepareNoteHtml = async (
	content: string,
	/** Seeds the generated filenames; surfaces pass their entity id. */
	seed: string,
	options?: {
		folder?: string
		/**
		 * Images the author took from the library rather than the device
		 * (MYD-470). They are already on Cloudinary and their URLs are already in
		 * the body, so nothing is uploaded for them. They are reported alongside
		 * the real uploads so the caller writes a row for each without needing to
		 * know where the picture came from.
		 */
		libraryImages?: LibraryImage[]
	},
): Promise<{ html: string; uploaded: UploadedNoteImage[] }> => {
	const files = convertRTEImages(content, seed)

	const uploaded: UploadedNoteImage[] = []
	for (const file of files) {
		uploaded.push(await uploadToCloudinary(file, options?.folder ? { folder: options.folder } : undefined))
	}

	// With no embedded images this leaves the HTML untouched. It is given the
	// real uploads alone: the swap is positional, and a library image has no
	// `data:image` in the body for it to consume.
	const html = modifyNote(content, uploaded)

	// Library images go AFTER the uploads. The swap above indexes from zero, so
	// this ordering is what keeps the two concerns from interfering; a library
	// image ahead of an upload would hand an embed the wrong URL.
	return { html, uploaded: [...uploaded, ...(options?.libraryImages || []).map(asUploaded)] }
}

/**
 * A library image in the shape the note surfaces already store.
 *
 * A library image is already on Cloudinary, so there is no upload response to
 * describe it. This fabricates the equivalent, which is what lets a caller
 * write its gallery row without caring where the picture came from.
 */
const asUploaded = (image: LibraryImage): UploadedNoteImage => ({
	public_id: image.uuid || cloudinaryPublicId(image.url) || '',
	original_filename: image.name || image.context || 'image',
	bytes: image.size || 0,
	secure_url: image.url,
	format: image.type || '',
})

export default prepareNoteHtml
